import { IOrganization, IScopeClauseClause } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { LinkRouter } from "@/components/shared/buttons/LinkRouter";
import { theme } from "@/config/project";
import {
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    Typography,
} from "@mui/material";
import { DialogProps } from "@toolpad/core/useDialogs";

interface IClauseLinkProps {
    clause: IScopeClauseClause;
    organization: IOrganization;
}

const ClauseLink = ({ clause, organization }: IClauseLinkProps) => {
    const linkUrl = `/organizations/clause-library?clauseId=${clause.id}`;
    return (
        <Typography variant="caption">
            View{" "}
            <LinkRouter to={linkUrl} variant="caption" target="_blank">
                {clause.title}
            </LinkRouter>{" "}
            in the {organization.name} Clause Library
        </Typography>
    );
};

export interface IGuidanceConfirmationDialogProps {
    /**
     * The clause being referenced for the guidance
     */
    clause: IScopeClauseClause;
    /**
     * The new guidance to apply
     */
    newGuidance: string;
    /**
     * The organization for generating clause links
     */
    organization: IOrganization;
    /**
     * Callback to cancel the dialog
     */
    onCancel: () => void;
    /**
     * Callback to confirm the dialog
     */
    onConfirm: () => void;
}

export const GuidanceConfirmationDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IGuidanceConfirmationDialogProps>) => {
    const { clause, newGuidance, organization, onCancel, onConfirm } = payload;

    const handleCancel = () => {
        onCancel();
        onClose();
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Dialog open={open} onClose={() => onClose()}>
            <DialogContent sx={{ p: 5 }}>
                <Stack spacing={2}>
                    <Stack alignItems={"center"}>
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            textTransform={"capitalize"}
                            textAlign={"center"}
                        >
                            {clause.title}
                        </Typography>
                        <Typography>
                            Edit the guidance for this clause?
                        </Typography>
                    </Stack>

                    <Stack
                        sx={{
                            bgcolor: theme.palette.background.default,
                            p: 1.5,
                            width: "100%",
                            borderRadius: 2,
                        }}
                    >
                        <Typography fontWeight={500} gutterBottom>
                            New Guidance:
                        </Typography>
                        <Typography>"{newGuidance}"</Typography>
                    </Stack>
                    <ClauseLink clause={clause} organization={organization} />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 5, pb: 5, justifyContent: "center" }}>
                <Button
                    onClick={handleCancel}
                    variant="text"
                    color="error"
                    sx={{
                        borderRadius: 2,
                        px: 2.5,
                        textTransform: "capitalize",
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={() => handleConfirm()}
                    variant="contained"
                    color="primary"
                    sx={{
                        borderRadius: 2,
                        px: 2.5,
                        textTransform: "capitalize",
                    }}
                >
                    Save to Scope
                </Button>
            </DialogActions>
        </Dialog>
    );
};
